"use client";

import { useEffect } from "react";
import { create } from "zustand";
import { useProjectConfigs } from "@/features/project/ProjectContext";

export type ListViewType = "list" | "cards";

interface ListViewTypeState {
  viewType: ListViewType | null;
  setViewType: (viewType: ListViewType) => void;
}

export const useListViewTypeStore = create<ListViewTypeState>()((set) => ({
  viewType: null,
  setViewType: (viewType) => set({ viewType }),
}));

/**
 * Whether results show as a list or a grid of cards.
 *
 * Seeded from the project's `defaultResultListView` in an effect rather than at
 * store creation, for the same reason as `useItemsOrder`: the store is
 * module-level while the config is per-tenant and per-request, so initialising
 * from config at module scope would bake the first tenant's default in for every
 * later one.
 *
 * Cards are a desktop-only choice — the mobile layout is always a list — so the
 * breakpoint is handled where the views are chosen, not here.
 */
export function useListViewType() {
  const { defaultResultListView } = useProjectConfigs();
  const viewType = useListViewTypeStore((state) => state.viewType);
  const setViewType = useListViewTypeStore((state) => state.setViewType);

  useEffect(() => {
    if (viewType) return;
    setViewType(defaultResultListView === "cards" ? "cards" : "list");
  }, [defaultResultListView, viewType, setViewType]);

  // Before the effect runs, and for a project that configures nothing, a list.
  return { viewType: viewType ?? "list", setViewType };
}
